import { ApiError } from './errors';
import type { PresignUploadResult } from './endpoints';

/**
 * Upload a file straight to S3 using a presigned PUT URL.
 *
 * This deliberately bypasses the envelope {@link ApiClient}: the presigned URL
 * already carries its own (short-lived) authorization in the query string, so we
 * must NOT attach the Cognito bearer, must NOT send JSON, and there is no
 * `ApiResponse<T>` envelope to parse — S3 replies with a bare 200 and an empty
 * body. The browser PUTs the raw `File` and echoes exactly the headers the
 * presign step told us to (typically `Content-Type`), or S3 rejects the
 * signature. No secrets are involved — the backend signed the request.
 *
 * Throws {@link ApiError} on any non-2xx / network failure so callers handle a
 * single error type, consistent with the rest of the client.
 */
export async function putToPresignedUrl(
  presigned: Pick<PresignUploadResult, 'uploadUrl' | 'headers'>,
  file: File | Blob,
  options: { signal?: AbortSignal; fetchImpl?: typeof fetch; contentType?: string } = {},
): Promise<void> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  // S3 validates the PUT against the exact Content-Type that was signed. Echo
  // any header the presign step returned, then ensure Content-Type matches what
  // the caller sent to presign (`options.contentType`) — falling back to the
  // File's own type. Getting this wrong fails the signature, so be explicit.
  const headers: Record<string, string> = { ...presigned.headers };
  const contentType =
    options.contentType ?? (file instanceof File ? file.type : undefined) ?? undefined;
  if (contentType && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = contentType;
  }

  let res: Response;
  try {
    res = await fetchImpl(presigned.uploadUrl, {
      method: 'PUT',
      headers,
      body: file,
      signal: options.signal,
    });
  } catch (cause) {
    const aborted = cause instanceof DOMException && cause.name === 'AbortError';
    throw new ApiError({
      code: aborted ? 'RATE_LIMITED' : 'INTERNAL',
      message: aborted
        ? 'La subida del archivo tardó demasiado. Intenta de nuevo.'
        : 'No se pudo subir el archivo. Revisa tu conexión e intenta de nuevo.',
    });
  }

  if (!res.ok) {
    throw new ApiError({
      code: 'INTERNAL',
      message: 'No se pudo subir el archivo. Vuelve a intentarlo.',
      status: res.status,
    });
  }
}
